import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useTranslation } from 'react-i18next';

/**
 * Action types for user management mutations
 */
type UserMutationAction = 'adding' | 'revoking' | 'promoting';

/**
 * Shows a toast notification for user management mutations with consistent messaging.
 * Handles both cases where user name is available and generic cases.
 *
 * @param mutation - The promise returned by the mutation
 * @param t - Translation function from react-i18next
 * @param action - The type of action being performed
 * @param userName - Optional user's full name for personalized messages
 * @returns The promise from toast.promise for chaining
 *
 * @example
 * ```tsx
 * showUserMutationToast(
 *   grantAccessMutation.mutateAsync(userId),
 *   t,
 *   'adding',
 *   user.full_name
 * );
 * ```
 */
export function showUserMutationToast(
  mutation: Promise<any>,
  t: ReturnType<typeof useTranslation>['t'],
  action: UserMutationAction,
  userName?: string,
) {
  return toast.promise(mutation, {
    loading: userName
      ? t(`user_management.${action}_user`, { name: userName, ns: 'general' })
      : t(`user_management.${action}`, { ns: 'general' }),
    success: () =>
      userName
        ? t(
            `user_management.${action === 'adding' ? 'added' : action === 'revoking' ? 'revoked' : 'promoted'}_success`,
            {
              name: userName,
              ns: 'general',
            },
          )
        : t(
            `user_management.${
              action === 'adding' ? 'added' : action === 'revoking' ? 'revoked' : 'promoted'
            }_success_generic`,
            {
              ns: 'general',
            },
          ),
    error: (error: Error) =>
      userName
        ? t(`user_management.${action === 'adding' ? 'added' : action === 'revoking' ? 'revoked' : 'promoted'}_error`, {
            name: userName,
            error: error.message,
            ns: 'general',
          })
        : t(
            `user_management.${
              action === 'adding' ? 'added' : action === 'revoking' ? 'revoked' : 'promoted'
            }_error_generic`,
            {
              error: error.message,
              ns: 'general',
            },
          ),
  });
}
